import { useState } from 'react'
import type { AdminPaymentSummary } from '@/types/api'
import { Dialog } from '@/components/ui/Dialog'
import { PaymentDetailsPanel } from './PaymentDetailsPanel'
import { VerifyPaymentDialog, type VerificationDecision } from './VerifyPaymentDialog'

interface PaymentDetailsDialogProps {
  /** The booking's id. A trip must pass `trip.requestId`. */
  tripRequestId: number | null
  bookingReference: string | null
  payment: AdminPaymentSummary | null
  onClose: () => void
}

/**
 * The payment record for one booking, as a dialog.
 *
 * The Trips page already has a details drawer and embeds
 * {@link PaymentDetailsPanel} directly. The Trip Requests page has no drawer —
 * it is an action queue — so this gives that screen the same view without
 * building a second drawer around it.
 */
export function PaymentDetailsDialog({
  tripRequestId,
  bookingReference,
  payment,
  onClose,
}: PaymentDetailsDialogProps) {
  const [decision, setDecision] = useState<VerificationDecision | null>(null)

  return (
    <>
      <Dialog
        open={tripRequestId !== null && payment !== null}
        onClose={onClose}
        title={`Payment · ${bookingReference ?? (tripRequestId ? `#${tripRequestId}` : '')}`}
        description="How this booking was paid for, and the proof behind it."
        size="lg"
      >
        <PaymentDetailsPanel
          tripRequestId={tripRequestId}
          payment={payment}
          bookingReference={bookingReference}
          onVerify={() => setDecision('verify')}
          onReject={() => setDecision('reject')}
          onChanged={onClose}
        />
      </Dialog>

      <VerifyPaymentDialog
        tripRequestId={decision ? tripRequestId : null}
        bookingReference={bookingReference}
        payment={payment}
        decision={decision ?? 'verify'}
        onClose={() => {
          setDecision(null)
          // The summary this dialog was rendering is now stale — the mutation
          // invalidated the list it came from, so closing both sends the
          // operator back to a row that reflects their decision.
          onClose()
        }}
      />
    </>
  )
}
